/*
 * @Author: Antes
 * @Email: tangzhlyd@gmail.com
 * @Date: 2021/12/14 14:24
 * @Description: 用于表格类 发送请求 自动管理分页  会自动在请求headers里面追加 routeCode
 */
import {
  append,
  clone,
  head,
  length,
  isEmpty,
  isNil,
  last,
  mergeDeepRight,
  mergeLeft,
  mergeRight,
  pick,
} from "ramda";
import { useUpdate } from "@/hooks";
import type { FilterValue, TablePaginationConfig } from "antd/lib/table/interface";
import { isArray, isObject } from "@/utils";
import { useCallback, useRef, useEffect } from "react";
import { useRoute } from "@/hooks/useRoute";
export enum ViewType {
  PAGE = "page",
  INFO = "info",
}
interface Options<R, P extends any[]> {
  manual?: boolean; // 加载 是否自动发送
  onSuccess?: (data: R, params: P) => void; // 成功回调
  onError?: (e: string, params: P) => void; // 失败回调
  defaultLoading?: boolean; // 默认 loading 状态
  defaultParams?: P;
  defaultPagination?: {
    pageSize: number;
    current: number;
  };
  currentKey?: string;
  sizeKey?: string;
  paginationByTableToFun?: (value?: Pagination) => { [index: string]: number }; // 把 useTable 的分页参数 改造成 请求函数的分页参数
  funToTable?: (data: any) => Pagination; // 请求函数的分页参数 改造成 useTable 的分页参数
  noLoading?: boolean;
  defaultOrders?: { asc: boolean; column: string }[];
  runBefore?: (params: P) => P;
}
export interface Pagination {
  current: number;
  pageSize: number;
  pageNumber?: number;
  // size?: number
  total?: number;
  records?: any;
}
function paginationByTableToFun({ current, pageSize } = { current: 1, pageSize: 10 }) {
  return { size: pageSize, current, pageNumber: current, pageSize };
}
function paginationByFunToTable(data: any): Pagination {
  return isNil(data)
    ? {
        current: 1,
        pageSize: 10,
        pageNumber: 1,
        total: 0,
        records: undefined,
      }
    : {
        current: (data?.data?.pageNum ?? data?.data?.current) || 1,
        pageSize: (data?.data?.pageSize ?? data?.data?.size) || 10,
        pageNumber: data?.data?.current,
        total: data?.data?.total,
        records: data?.data?.records ?? data?.data?.list,
      };
}
interface UseTableState<T, P extends any[]> {
  code?: string;
  loading: boolean;
  data?: T;
  params: P;

  isValidating: boolean;
}
interface Props {
  presetsLen: number;
  actualLen: number;
}
export function handleParam<T extends any[]>(
  param: T,
  { presetsLen, actualLen }: Props,
  res: any,
): T {
  if (isNil(res)) {
    return param;
  }
  const is = presetsLen > actualLen;
  if (is) {
    // 函数预设参数大于1个 && 传入的是个空 通常查询接口 允许传空字符串
    if (presetsLen > 1 && isEmpty(param)) {
      return append(res, [{}]) as T;
    }
    return append(res, param) as T;
  } else {
    const item = last(param);
    if (isObject(item) && param.length) {
      const data = clone(param);
      data.splice(length(param) - 1, 1, mergeDeepRight(item, res));
      return data;
    }
  }
  return param;
}
const defaultOptions = {
  funToTable: paginationByFunToTable,
  paginationByTableToFun: paginationByTableToFun, // tableToFun
  currentKey: "current",
  sizeKey: "size",
  defaultLoading: false,
  manual: false,
  defaultPagination: {
    pageSize: 10,
    current: 1,
  },
};
type Service<TData, TParams extends any[]> = (...args: TParams) => Promise<TData>;
type Simplify<T> = {
  [P in keyof T]: T[P];
};
type RequiredPick<T, K extends keyof T> = Simplify<
  Required<Pick<T, K>> & Partial<Pick<T, Exclude<keyof T, K>>>
>;
export default function useTable<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options: Options<TData, TParams> = {
    defaultLoading: false,
    manual: false,
  },
) {
  type ThisBaseOptions = Options<TData, TParams>;
  type RequiredPBaseOptions = RequiredPick<ThisBaseOptions, keyof typeof defaultOptions>;
  const update = useUpdate();
  const _options = mergeLeft(options, defaultOptions);
  const optionsRef = useRef<RequiredPBaseOptions>(_options);
  optionsRef.current = _options;
  const { code } = useRoute();
  const state = useRef<UseTableState<TData, TParams>>({
    loading: false,
    isValidating: false,
    params: (_options.defaultParams ?? []) as TParams,
  });
  state.current.code = code;
  // optionsRef.current.funToTable = optionsRef.current.funToTable ?? paginationByFunToTable
  // optionsRef.current.paginationByTableToFun = optionsRef.current.paginationByTableToFun ?? paginationByTableToFun // tableToFun
  // optionsRef.current.currentKey = optionsRef.current.currentKey ?? 'current'
  // optionsRef.current.sizeKey = optionsRef.current.sizeKey ?? 'size'
  const serviceRef = useRef(service);
  useEffect(() => {
    serviceRef.current = service;
  }, [service]);
  const pagination = useRef<Pagination>(optionsRef.current.defaultPagination);
  const run = useCallback(
    (...param: TParams): Promise<TData> => {
      if (state.current.isValidating) {
        return new Promise(() => {
          // console.log('isLoading')
        });
      }
      const thisParam = handleParam(
        param,
        { presetsLen: serviceRef.current.length, actualLen: param.length },
        {
          headers: {
            routeCode: state.current.code,
          },
        },
      );
      state.current.isValidating = true;
      if (!optionsRef.current.noLoading) {
        update();
      }
      return new Promise((resolve) => {
        serviceRef
          .current(
            ...(optionsRef.current.runBefore ? optionsRef.current.runBefore(thisParam) : thisParam),
          )
          .then((item) => {
            if (optionsRef.current.onSuccess) {
              optionsRef.current.onSuccess(item, param);
            }
            pagination.current = pick(
              ["current", "pageSize", "total"],
              optionsRef.current.funToTable(item),
            );
            state.current.data = item;
            resolve(item);
          })
          .catch((err: string) => {
            if (optionsRef.current.onError) {
              optionsRef.current.onError(err, param);
            }
            state.current.data = undefined;
          })
          .finally(() => {
            state.current.params = param;
            state.current.isValidating = false;
            state.current.loading = false;
            update();
          });
      });
    },
    [update],
  );

  const refresh = useCallback(() => {
    return run(...(state.current.params ?? []));
  }, [run]);

  // 首次 判断是否默认执行 manual = true 不会默认执行
  useEffect(() => {
    if (!optionsRef.current.manual) {
      const data = optionsRef.current.defaultParams;
      if (data && isArray(data)) {
        data[0] = mergeRight(head(data), { orders: optionsRef.current.defaultOrders });
      }
      run(...((data ?? []) as TParams));
    }
    // eslint-disable-next-line
  }, []);

  const search = useCallback(
    (...param: TParams) => {
      param[0] = {
        orders: optionsRef.current.defaultOrders,
        ...optionsRef.current.paginationByTableToFun(pagination.current),
        [optionsRef.current.sizeKey as string]: 10,
        ...param[0],
        [optionsRef.current.currentKey as string]: 1,
      };

      return run(...param);
    },
    [run],
  );

  const hdChange = useCallback(
    (
      { pageSize: size, current }: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: any,
    ) => {
      pagination.current = {
        current: current ?? pagination.current.current,
        pageSize: size ?? pagination.current.pageSize,
        // size: size ?? pagination.current.pageSize,
        pageNumber: current ?? pagination.current.pageNumber,
        total: pagination.current.total,
      };
      if (state.current.params) {
        state.current.params.splice(0, 1, {
          ...state.current.params[0],
          orders:
            isEmpty(sorter) || isNil(sorter.order)
              ? optionsRef.current.defaultOrders
              : [
                  {
                    column: sorter.field,
                    asc: sorter.order.includes("asc"),
                  },
                ],
          ...optionsRef.current.paginationByTableToFun(pagination.current),
        });
        run(...state.current.params);
      } else {
        const data = state.current.params;
        run(...data);
      }
    },
    [run],
  );
  const tableProps = {
    dataSource: optionsRef.current.funToTable(state.current.data)?.records,
    loading: optionsRef.current.noLoading ? undefined : state.current.loading,
    onChange: hdChange,
    pagination: {
      ...pagination.current,
      showTotal,
      showQuickJumper: (pagination.current.total ?? 0) / pagination.current.pageSize > 5,
      hideOnSinglePage: true,
      showSizeChanger: true,
    },
  };
  return { run, search, refresh, tableProps, ...state.current };
}
function showTotal(total: number) {
  return `共 ${total} 条`;
}
